import React, { useEffect, useRef, useState } from "react";

// Loads Cloudflare's Turnstile script once and renders the widget into a div. No npm package
// needed — Cloudflare only ships this as a plain <script>, so we load it the same way.
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          appearance?: "always" | "execute" | "interaction-only";
        }
      ) => string;
      reset: (widgetId?: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_LOAD_TIMEOUT_MS = 8000;

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
}

// Renders nothing (and never calls onToken) when REACT_APP_TURNSTILE_SITE_KEY isn't set — matches
// the backend's fail-open behavior so forgot-password keeps working before real Cloudflare keys
// are configured. Once a real key IS set, though, the backend strictly requires a valid token, so
// a script-load failure here (ad-blocker/privacy extension blocking challenges.cloudflare.com,
// network hiccup, Cloudflare outage) can't be silently worked around — the important thing is
// surfacing that clearly instead of leaving the submit button disabled forever with no explanation.
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onToken }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return;
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        setLoadFailed(true);
      }
    }, SCRIPT_LOAD_TIMEOUT_MS);

    loadTurnstileScript()
      .then(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        setScriptReady(true);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        setLoadFailed(true);
      });

    return () => clearTimeout(timeoutId);
  }, [siteKey]);

  useEffect(() => {
    if (!scriptReady || !siteKey || !containerRef.current || !window.turnstile) return;
    window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      appearance: "interaction-only", // stays invisible unless Cloudflare decides a challenge is needed
      callback: onToken,
      "error-callback": () => setLoadFailed(true),
      "expired-callback": () => onToken(""),
    });
    // Deliberately no cleanup/re-render on onToken changes — the widget is rendered once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady, siteKey]);

  if (!siteKey) return null;

  if (loadFailed) {
    return (
      <div style={{ color: "#d32f2f", fontSize: "0.8rem", textAlign: "center", maxWidth: 320 }}>
        Security check couldn't load. If you have an ad-blocker or privacy extension enabled,
        please disable it for this site and refresh the page.
      </div>
    );
  }

  return <div ref={containerRef} />;
};

export default TurnstileWidget;
